<script setup lang="ts" generic="T = string">
/**
 * An ordered list the user edits row by row: numbered rows, move up/down,
 * delete, „+ add".
 *
 * Two shapes:
 * - **Strings** (default): each row is a text input. Enter moves to the next
 *   row (or adds one at the end), Alt + ↑/↓ moves the row.
 * - **Objects**: the `#row` slot renders a row's fields (`item`, `index`,
 *   `label`, `update(next)`) — a milestone with its task count, a protocol
 *   field with label, type and options. The list keeps the numbering, the
 *   reordering and the delete button; `createItem` builds the row „+ add"
 *   appends. Enter in the row's last text field does what a string row's Enter
 *   does (a field that needs Enter keeps it). Alt + ↑/↓ moves the row from any
 *   field in it that does not claim the keys itself (a select opening its list
 *   does).
 *
 * Enter with Ctrl, Cmd, Shift or Alt stays with the field. Inside a form that
 * has a submit control (a submit button or input, or a button with no type)
 * Enter stays with the form, so implicit submission still saves.
 *
 * Every row has a grip in front of its number. Dragging it, or lifting it with
 * Space and moving with the arrow keys, writes the new order once — the same
 * write ↑/↓ makes. The list does not drop empty rows; the caller does.
 *
 * `canRemove` locks the delete button of single rows — a milestone that still
 * has tasks. The row stays editable and movable.
 */
import { nextTick, useTemplateRef, watch } from 'vue';
import GripVertical from 'lucide-vue-next/dist/esm/icons/grip-vertical.js';
import { useBrowserAutofillOffAttrs } from './browserAutofill';
import { AddIcon, ArrowDownIcon, ArrowUpIcon, DeleteIcon } from '@blpde/nx-icons';
import NxIconButton from '../IconButton/NxIconButton.vue';
import NxSaveStateBadge from './NxSaveStateBadge.vue';
import { useInputSaveStatus } from './composables/useInputSaveStatus';
import { reorderItems } from '../Sortable/sortMath';
import { useNxSortable } from '../Sortable/useNxSortable';

// Plain text input: stays out of browser autofill in a provideBrowserAutofillOff() scope.
const autofillAttrs = useBrowserAutofillOffAttrs()('text', undefined);
const props = withDefaults(
  defineProps<{
    addLabel: string;
    placeholder?: string;
    /** Accessible name stem for the rows, e.g. "Kategorie" → "Kategorie 1". */
    itemLabel?: string;
    moveUpLabel: string;
    moveDownLabel: string;
    deleteLabel: string;
    /**
     * Builds the row „+ add" appends. Needed for object rows; a string list
     * falls back to an empty string.
     */
    createItem?: () => T;
    /** `false` locks the row's delete button; the row stays editable. */
    canRemove?: (item: T, index: number) => boolean;
    /** Tooltip and accessible name of a locked delete button. */
    removeLockedLabel?: string;
  }>(),
  {
    placeholder: undefined,
    itemLabel: undefined,
    createItem: undefined,
    canRemove: undefined,
    removeLockedLabel: 'Löschen nicht möglich',
  }
);

const model = defineModel<T[]>({ required: true });

// Save status (loading/success/error) from an ancestor NxLoadingWrapper. The
// default row inputs carry `aria-invalid` / `aria-busy`. While it saves, the
// list locks like the other inputs: the default row inputs, the grips, the row
// buttons, „+ add", Enter and Alt + ↑/↓. The fields of a custom `#row` belong
// to the caller, who binds them there (the wrapper's slot hands out `disabled`).
const {
  visualState,
  errorMessage,
  fadeOut,
  srText,
  ariaBindings,
  disabled: isDisabled,
} = useInputSaveStatus();

defineSlots<{
  /** A row's own fields; without it every row is a text input over a string. */
  row?: (props: { item: T; index: number; label: string; update: (next: T) => void }) => unknown;
}>();

/** Reset each render pass; filled via function refs on the row inputs. */
let inputEls: (HTMLInputElement | null)[] = [];
function setInputRef(el: unknown, index: number) {
  if (index === 0) inputEls = [];
  inputEls[index] = (el as HTMLInputElement | null) ?? null;
}
function focusInput(index: number) {
  inputEls[index]?.focus();
}

/** The same for the boxes that hold a slot row's fields. */
let contentEls: (HTMLElement | null)[] = [];
function setContentRef(el: unknown, index: number) {
  if (index === 0) contentEls = [];
  contentEls[index] = (el as HTMLElement | null) ?? null;
}
const FOCUSABLE =
  'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';
function focusablesIn(index: number): HTMLElement[] {
  const el = contentEls[index];
  return el ? Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE)) : [];
}

function updateAt(index: number, value: T) {
  const next = model.value.slice();
  next[index] = value;
  model.value = next;
}
function move(index: number, delta: number) {
  const target = index + delta;
  if (target < 0 || target >= model.value.length) return;
  const next = model.value.slice();
  [next[index], next[target]] = [next[target], next[index]];
  model.value = next;
}
function removable(entry: T, index: number): boolean {
  return props.canRemove ? props.canRemove(entry, index) : true;
}
function remove(index: number) {
  if (!removable(model.value[index], index)) return;
  model.value = model.value.filter((_, i) => i !== index);
}
function add() {
  model.value = [...model.value, props.createItem ? props.createItem() : ('' as unknown as T)];
}
function rowLabel(entry: T, index: number): string {
  if (props.itemLabel) return `${props.itemLabel} ${index + 1}`;
  return (typeof entry === 'string' && entry) || `${index + 1}`;
}

const listEl = useTemplateRef<HTMLElement>('list');

// One write on drop, like ↑/↓. The grip's own keyboard (Space, arrows, Escape)
// and its German announcements come from the sortable; Alt + ↑/↓ on a field
// stays on the field.
const sortable = useNxSortable<T>({
  items: () => model.value,
  container: listEl,
  disabled: () => isDisabled.value,
  labelOf: (item, index) => rowLabel(item, index),
  onReorder: (from, to) => {
    // Saving can start after the pointer went down. Drop nothing, and drop
    // the gesture: the lock at pointerdown has already been passed.
    if (isDisabled.value) {
      sortable.cancel();
      return;
    }
    model.value = reorderItems(model.value, from, to);
    // Rows are keyed by position, so the grip under the pointer is the old
    // index after the swap. The keyboard path focuses the moved row; a
    // pointer drop does the same.
    focusGrip(to);
  },
});

watch(isDisabled, (disabled) => {
  if (disabled) sortable.cancel();
});

function focusGrip(index: number) {
  void nextTick(() => {
    listEl.value
      ?.querySelector<HTMLElement>(`[data-test="drag"][data-nx-sortable-index="${index}"]`)
      ?.focus();
  });
}

/** Single-line text entry. A select, a textarea, a listbox and an open popup keep Enter. */

function modifierHeld(e: KeyboardEvent): boolean {
  return e.altKey || e.ctrlKey || e.metaKey || e.shiftKey;
}

/**
 * A form that can submit owns Enter (implicit submission). A button with no
 * type submits too. The list's own buttons are `type="button"`.
 */
const SUBMIT_CONTROL = 'button[type="submit"], input[type="submit"], button:not([type])';
function formOwnsEnter(origin: EventTarget | null): boolean {
  if (!(origin instanceof Element)) return false;
  const form = origin.closest('form');
  return form?.querySelector(SUBMIT_CONTROL) != null;
}
const TEXT_TYPES = new Set(['text', 'search', 'email', 'url', 'tel', 'password', 'number']);
function isTextField(el: HTMLElement): boolean {
  if (el.getAttribute('role') === 'textbox') return true;
  if (el.tagName !== 'INPUT') return false;
  const type = (el.getAttribute('type') || 'text').toLowerCase();
  return TEXT_TYPES.has(type);
}
function textFieldOf(origin: HTMLElement): HTMLElement | null {
  const field = isTextField(origin)
    ? origin
    : origin.closest<HTMLElement>('input, [role="textbox"]');
  return field && isTextField(field) ? field : null;
}

/**
 * Where `target` sits after a caller drops rows on blur. Object rows are found
 * by identity. Anything else shifts back by the number of rows that vanished,
 * which is the row the user left.
 */
function indexAfterShrink(before: number, index: number, target: T): number {
  if (model.value.length === 0) return -1;
  if (target !== null && typeof target === 'object') {
    const found = model.value.indexOf(target);
    if (found >= 0) return found;
  }
  const removed = Math.max(0, before - model.value.length);
  return Math.min(Math.max(index - removed, 0), model.value.length - 1);
}

/**
 * Focus a field of the row Enter moved to. The caller may drop the row we
 * leave when it blurs (an empty draft). Rows are keyed by index, so that
 * removal unmounts the successor just focused and the browser lands on the
 * document. After Vue applies the shorter list, focus that successor again.
 */
function focusRowAfterEnter(index: number, fieldAt: (index: number) => HTMLElement | undefined) {
  const before = model.value.length;
  const target = model.value[index];
  const field = fieldAt(index);
  field?.focus();
  void nextTick(() => {
    if (field?.isConnected) return;
    const at = indexAfterShrink(before, index, target);
    if (at < 0) return;
    fieldAt(at)?.focus();
  });
}

/**
 * Enter in the last text field of a slot row. The next row's first focusable
 * is the same place „+ add" lands; the last row appends one. Saving locks it
 * like the move buttons. A field that needs the key keeps it.
 */
function onRowEnter(e: KeyboardEvent, index: number) {
  if (isDisabled.value || e.defaultPrevented || modifierHeld(e) || formOwnsEnter(e.target)) return;
  const origin = e.target as HTMLElement | null;
  if (!origin?.closest) return;
  if (origin.closest('select, textarea, [contenteditable="true"], [role="listbox"]')) return;
  if (origin.closest('[aria-expanded="true"]')) return;
  const field = textFieldOf(origin);
  if (!field) return;
  const textFields = focusablesIn(index).filter(isTextField);
  if (textFields[textFields.length - 1] !== field) return;
  e.preventDefault();
  if (index < model.value.length - 1) {
    focusRowAfterEnter(index + 1, (at) => focusablesIn(at)[0]);
    return;
  }
  const next = index + 1;
  add();
  nextTick(() => focusablesIn(next)[0]?.focus());
}

/**
 * „+ add". A string list keeps its old behaviour (the new input waits for a
 * click or Enter); an object list lands in the new row's first field, since
 * its row has no single input the user would otherwise type into.
 */
function onAddClick(slotRows: boolean) {
  // Read before adding: a v-model bound by the parent only reflects the new
  // row after the parent has re-rendered.
  const index = model.value.length;
  add();
  if (!slotRows) return;
  nextTick(() => focusablesIn(index)[0]?.focus());
}

function onInputKeydown(e: KeyboardEvent, index: number) {
  // Enter during IME composition confirms the candidate, not the row.
  if (e.isComposing) return;
  if (e.key === 'Enter') {
    if (modifierHeld(e) || formOwnsEnter(e.target)) return;
    e.preventDefault();
    if (index < model.value.length - 1) {
      focusRowAfterEnter(index + 1, (at) => inputEls[at] ?? undefined);
    } else {
      add();
      nextTick(() => focusInput(index + 1));
    }
    return;
  }
  if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
    e.preventDefault();
    const delta = e.key === 'ArrowUp' ? -1 : 1;
    const target = index + delta;
    if (target < 0 || target >= model.value.length) return;
    move(index, delta);
    nextTick(() => focusInput(target));
  }
}

/**
 * Alt + ↑/↓ inside a slot row. Rows are keyed by position, so after the swap
 * the field the user was in belongs to the target row — found again by its
 * position among the row's focusable fields, and focused there.
 */
function onContentKeydown(e: KeyboardEvent, index: number) {
  // Enter during IME composition confirms the candidate, not the row.
  if (e.isComposing) return;
  if (e.key === 'Enter') {
    onRowEnter(e, index);
    return;
  }
  if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
  // Saving: the move buttons are locked, so the shortcut is too.
  if (isDisabled.value) return;
  // A field that claims the keys (a select opening its list) keeps them, and
  // so do native controls that give Alt + arrows a meaning of their own.
  if (e.defaultPrevented) return;
  const origin = e.target as HTMLElement | null;
  if (origin?.closest('select, textarea, [contenteditable="true"]')) return;
  e.preventDefault();
  const delta = e.key === 'ArrowUp' ? -1 : 1;
  const target = index + delta;
  if (target < 0 || target >= model.value.length) return;
  const position = origin ? focusablesIn(index).indexOf(origin) : -1;
  move(index, delta);
  nextTick(() => focusablesIn(target)[Math.max(position, 0)]?.focus());
}
</script>

<template>
  <div ref="list" class="nx-editable-list" :data-save-status="visualState">
    <div
      v-for="(entry, index) in model"
      :key="index"
      class="nx-editable-list__row"
      v-bind="sortable.itemProps(index)"
    >
      <button
        type="button"
        class="nx-editable-list__grip"
        data-test="drag"
        v-bind="sortable.handleProps(index)"
        :disabled="isDisabled"
      >
        <GripVertical aria-hidden="true" />
      </button>
      <span class="nx-editable-list__num">{{ index + 1 }}</span>
      <div
        v-if="$slots.row"
        :ref="(el) => setContentRef(el, index)"
        class="nx-editable-list__content"
        role="group"
        :aria-label="rowLabel(entry, index)"
        @keydown="onContentKeydown($event, index)"
      >
        <slot
          name="row"
          :item="entry"
          :index="index"
          :label="rowLabel(entry, index)"
          :update="(next: T) => updateAt(index, next)"
        />
      </div>
      <input
        v-else
        :ref="(el) => setInputRef(el, index)"
        class="nx-editable-list__input"
        v-bind="{ ...autofillAttrs, ...ariaBindings }"
        :aria-label="rowLabel(entry, index)"
        :value="entry"
        :placeholder="placeholder"
        :disabled="isDisabled"
        @input="updateAt(index, ($event.target as HTMLInputElement).value as unknown as T)"
        @keydown="onInputKeydown($event, index)"
      />
      <NxIconButton
        class="nx-editable-list__action"
        data-test="move-up"
        :title="moveUpLabel"
        :disabled="isDisabled || index === 0"
        @click="move(index, -1)"
      >
        <ArrowUpIcon filled class="nxt-icon-md" />
      </NxIconButton>
      <NxIconButton
        class="nx-editable-list__action"
        data-test="move-down"
        :title="moveDownLabel"
        :disabled="isDisabled || index === model.length - 1"
        @click="move(index, 1)"
      >
        <ArrowDownIcon filled class="nxt-icon-md" />
      </NxIconButton>
      <NxIconButton
        class="nx-editable-list__action is-delete"
        data-test="delete"
        danger
        :title="removable(entry, index) ? deleteLabel : removeLockedLabel"
        :disabled="isDisabled || !removable(entry, index)"
        @click="remove(index)"
      >
        <DeleteIcon filled class="nxt-icon-md" />
      </NxIconButton>
    </div>
    <button
      type="button"
      class="nx-editable-list__add"
      data-test="add"
      :disabled="isDisabled"
      @click="onAddClick(!!$slots.row)"
    >
      <AddIcon filled class="nxt-icon-md" />
      {{ addLabel }}
    </button>
    <NxSaveStateBadge
      class="save-badge"
      :state="visualState"
      :error-message="errorMessage"
      :fade-out="fadeOut"
    />
    <span class="nx-sr-only" aria-live="polite">{{ srText }}</span>
  </div>
</template>

<style scoped>
/* `type="search"` only keeps browser autofill out (browserAutofill.ts): no
   search styling, no clear button. */
input[type='search'] {
  appearance: none;
}

input[type='search']::-webkit-search-cancel-button,
input[type='search']::-webkit-search-decoration {
  appearance: none;
  display: none;
}

.save-badge {
  position: absolute;
  top: calc(-1 * var(--nxt-space-md) * 0.438);
  right: calc(-1 * var(--nxt-space-md) * 0.438);
}
.nx-sr-only {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}
.nx-editable-list {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
}
.nx-editable-list__row {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  transition: box-shadow var(--nxt-motion-fast) var(--nxt-ease-out);
}
/* The dragged row follows the pointer; the bar marks where it would land.
   The surface fill is the same one NxSortableList paints on every item, so
   the row does not turn transparent once it leaves its place. */
.nx-editable-list__row[data-nx-sortable-state='pointer'] {
  background: var(--nxt-surface-bg);
  box-shadow: var(--nxt-shadow-lg);
  cursor: grabbing;
}
.nx-editable-list__row[data-nx-sortable-state='keyboard'] {
  outline: calc(var(--nxt-hairline) * 2) dashed var(--nxt-brand-bg);
  outline-offset: calc(var(--nxt-hairline) * 2);
  background: color-mix(in srgb, var(--nxt-brand-bg) 6%, var(--nxt-surface-bg));
}
.nx-editable-list__row[data-nx-drop-side]::after {
  content: '';
  position: absolute;
  inset-inline: 0;
  block-size: calc(var(--nxt-hairline) * 3);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-brand-bg);
  pointer-events: none;
}
.nx-editable-list__row[data-nx-drop-side='before']::after {
  top: calc(var(--nxt-space-xs) / -2 - var(--nxt-hairline) * 2);
}
.nx-editable-list__row[data-nx-drop-side='after']::after {
  bottom: calc(var(--nxt-space-xs) / -2 - var(--nxt-hairline) * 2);
}
.nx-editable-list__grip {
  display: inline-grid;
  flex: 0 0 auto;
  place-items: center;
  inline-size: var(--nxt-icon-size-xl);
  block-size: var(--nxt-icon-size-xl);
  padding: 0;
  border: 0;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-fg-subtle);
  cursor: grab;
}
.nx-editable-list__grip > svg {
  inline-size: var(--nxt-icon-size-md);
  block-size: var(--nxt-icon-size-md);
}
.nx-editable-list__grip:hover:not(:disabled) {
  background: var(--nxt-surface-sunken-bg);
  color: var(--nxt-fg);
}
.nx-editable-list__grip:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: var(--nxt-hairline);
}
.nx-editable-list__grip[aria-pressed='true'] {
  color: var(--nxt-brand-bg);
  cursor: grabbing;
}
.nx-editable-list__grip:disabled {
  color: var(--nxt-disabled-fg);
  cursor: not-allowed;
}
.nx-editable-list__num {
  min-width: 1.4em;
  text-align: right;
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-fg-muted);
}
.nx-editable-list__input {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  font: inherit;
  color: var(--nxt-fg);
  border-radius: var(--nxt-radius-sm);
}
.nx-editable-list__input:focus {
  outline: none;
  background: var(--nxt-input-bg-focus);
  box-shadow: inset 0 0 0 var(--nxt-hairline) var(--nxt-border-strong);
}
.nx-editable-list__input:disabled {
  color: var(--nxt-input-fg-disabled);
  cursor: not-allowed;
}
/* The caller's fields share the row's width; wrapping keeps a narrow dialog
   usable instead of pushing the row buttons out of view. */
.nx-editable-list__content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-xs);
}
/* Row buttons stay quiet until the row is in play; delete only turns red
   when it is itself hovered or focused, so it never shouts from every row. */
.nx-editable-list__row .nx-editable-list__action:not(:disabled) {
  color: var(--nxt-fg-subtle);
  transition: color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-editable-list__row:is(:hover, :focus-within) .nx-editable-list__action:not(:disabled) {
  color: var(--nxt-fg);
}
.nx-editable-list__row
  .nx-editable-list__action.is-delete:not(:disabled):is(:hover, :focus-visible) {
  color: var(--nxt-danger-subtle-fg);
}
@media (prefers-reduced-motion: reduce) {
  .nx-editable-list__row,
  .nx-editable-list__row[data-nx-sortable-state='pointer'],
  .nx-editable-list__row .nx-editable-list__action:not(:disabled) {
    transition: none;
  }
}
.nx-editable-list__add {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  align-self: flex-start;
  border: var(--nxt-hairline) dashed var(--nxt-border-strong);
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  padding: var(--nxt-space-2xs) var(--nxt-space-sm);
  color: var(--nxt-fg-muted);
  cursor: pointer;
  font: inherit;
}
.nx-editable-list__add:hover:not(:disabled) {
  color: var(--nxt-brand-bg);
  border-color: var(--nxt-brand-bg);
}
.nx-editable-list__add:disabled {
  color: var(--nxt-disabled-fg);
  border-color: var(--nxt-border);
  cursor: not-allowed;
}
</style>
